import React, { ComponentProps } from 'react';
import { Block, Flex, Text, Tooltip } from '@upsales/components';
import BemClass from '@upsales/components/Utils/bemClass';
import Client from 'App/resources/Model/Client';
import { useTranslation } from 'Components/Helpers/translate';
import { getDateString } from 'App/helpers/DateHelpers';
import { openDrawer } from 'Services/Drawer';
import CustomerPortfolioCompany, { BaseClient } from 'App/resources/Model/CustomerPortfolioCompany';
import { getCategoryDisplayText, getNumericRatingDisplay, getTextColor } from './helpers';

import './CreditRatingLabel.scss';

type Props = ComponentProps<typeof Block> & {
	prospectingCreditRating: Client['prospectingCreditRating'];
	prospectingNumericCreditRating?: Client['prospectingNumericCreditRating'];
	hideIfNoRating?: boolean;
	company?: CustomerPortfolioCompany | BaseClient;
	noDrawer?: boolean;
};

export default function CreditRatingLabel({
	prospectingCreditRating,
	prospectingNumericCreditRating,
	hideIfNoRating = false,
	company,
	noDrawer = false,
	...blockProps
}: Props) {
	const { t } = useTranslation();
	const classes = new BemClass('CreditRatingLabel');
	const companyMonitorChangeDate = company?.monitorChangeDate ?? null;

	const category = prospectingCreditRating === null ? 'missing' : prospectingCreditRating;
	const companyIsDeMonitored = !!company && !company?.isMonitored && category !== 'missing';
	const textColor = getTextColor(!!companyIsDeMonitored);
	const categoryDisplayText = getCategoryDisplayText(category, t);
	const numericRatingDisplay = getNumericRatingDisplay(prospectingNumericCreditRating, category);

	if (hideIfNoRating && prospectingCreditRating == null) {
		return null;
	}

	const tooltipTitle = `${t('event.creditMonitoring.removed')}${
		companyMonitorChangeDate ? `: ${getDateString(companyMonitorChangeDate, 'L')}` : ''
	}`;

	return (
		<Block
			className={classes
				.mod({
					[category]: true,
					hasCompany: company != null,
					isDeMonitored: companyIsDeMonitored && category !== 'missing'
				})
				.b()}
			{...blockProps}
			onClick={e => {
				e.stopPropagation();
				if (company && prospectingCreditRating != null && !noDrawer) {
					openDrawer('RiskDrawer', { company, valueType: null });
				}
			}}
		>
			<Tooltip distance={20} title={tooltipTitle} disabled={!companyIsDeMonitored}>
				<Flex>
					<Text
						size="sm"
						bold
						color={textColor}
						space={`mls pls prs ${category === 'other' || category === 'missing' ? 'mrs' : ''}`}
					>
						{categoryDisplayText}
					</Text>
					{numericRatingDisplay != null ? (
						<Text size="sm" bold color={textColor} space="pls prs mrs">
							{numericRatingDisplay}
						</Text>
					) : null}
				</Flex>
			</Tooltip>
		</Block>
	);
}
